import React from 'react';
import { parseISO } from 'date-fns';
import { Cruise } from '../context/CruiseContext';
import CustomCruiseIcon from './CustomCruiseIcon';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';
import { Calendar, MapPin, Globe } from 'lucide-react';

interface ShareCountdownImageFeedProps {
  cruise: Cruise;
  daysUntilDeparture: number;
  shipImageUrl?: string | null;
  itineraryTitle?: string | null;
}

const ShareCountdownImageFeed = React.forwardRef<HTMLDivElement, ShareCountdownImageFeedProps>(
  ({ cruise, daysUntilDeparture, shipImageUrl, itineraryTitle }, ref) => {
    const { formatDate } = useDisplayPreferences();
    
    return (
      <div
        ref={ref}
        data-share-image="true"
        className="relative mx-auto"
        style={{
          width: '1200px',
          height: '630px',
          fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
          boxSizing: 'border-box',
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#070e1b'
        }}
      >
        {/* Background Ship Image - Untinted, bright and clear */}
        {shipImageUrl ? (
          <div className="absolute inset-0">
            <img
              src={shipImageUrl}
              alt=""
              className="w-full h-full object-cover"
              crossOrigin="anonymous"
              style={{ opacity: 1 }}
            />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-blue-900 to-indigo-950" />
        )}

        {/* Soft Vignette Edge Fade on the Border */}
        <div 
          className="absolute inset-0 pointer-events-none z-20"
          style={{
            boxShadow: 'inset 0 0 50px 15px rgba(5, 12, 24, 0.7)',
            boxSizing: 'border-box'
          }}
        />

        {/* 1) Top-Left Tight Glass Card: Countdown & Itinerary */}
        <div 
          style={{
            position: 'absolute',
            top: '32px',
            left: '32px',
            width: '420px',
            background: 'rgba(10, 25, 47, 0.76)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.22)',
            borderRadius: '20px',
            padding: '20px 24px',
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            boxSizing: 'border-box',
            zIndex: 10
          }}
        >
          {/* Itinerary Title (if exists - cleanly without AI gem icon) */}
          {itineraryTitle && (
            <div 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4px 14px',
                borderRadius: '9999px',
                background: 'rgba(20, 184, 166, 0.25)',
                border: '1px solid rgba(45, 212, 191, 0.5)',
                color: '#5eead4',
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '0.02em',
                marginBottom: '10px',
                boxShadow: '0 4px 12px rgba(20, 184, 166, 0.2)'
              }}
            >
              <span className="truncate max-w-[350px]">{itineraryTitle}</span>
            </div>
          )}

          {/* Countdown Display */}
          <div className="flex items-baseline gap-3">
            <div 
              style={{ 
                fontSize: '76px', 
                fontWeight: 900, 
                lineHeight: 0.9, 
                color: '#ffffff',
                letterSpacing: '-0.04em',
                textShadow: '0 4px 16px rgba(0, 0, 0, 0.6)'
              }}
            >
              {daysUntilDeparture}
            </div>
            <div className="flex flex-col justify-end">
              <span 
                style={{ 
                  fontSize: '20px', 
                  fontWeight: 800, 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.06em',
                  color: '#38bdf8',
                  lineHeight: 1.1
                }}
              >
                {daysUntilDeparture === 1 ? 'Day' : 'Days'}
              </span>
              <span 
                style={{ 
                  fontSize: '12px', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.1em',
                  color: 'rgba(255, 255, 255, 0.75)',
                  marginTop: '2px'
                }}
              >
                Until Departure
              </span>
            </div>
          </div>
        </div>

        {/* 2) Top-Right Tight Glass Card: Cruising Along Advertisement + App Store & Google Play Callout */}
        <div
          style={{
            position: 'absolute',
            top: '32px',
            right: '32px',
            width: '440px',
            background: 'rgba(10, 25, 47, 0.76)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.22)',
            borderRadius: '20px',
            padding: '18px 22px',
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            boxSizing: 'border-box',
            zIndex: 10
          }}
        >
          {/* Logo & App Name */}
          <div className="flex items-center gap-3">
            <div 
              style={{
                padding: '8px',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <CustomCruiseIcon size={38} className="text-accent" />
            </div>

            <div className="flex-1 min-w-0">
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', lineHeight: 1.15 }}>
                Cruising Along
              </div>
              <div style={{ fontSize: '12px', color: '#93c5fd', marginTop: '2px', fontWeight: 500 }}>
                Track Your Cruise Adventures
              </div>
            </div>

            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '11px',
                fontWeight: 600,
                color: '#bfdbfe',
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                border: '1px solid rgba(255, 255, 255, 0.22)',
                padding: '4px 10px',
                borderRadius: '9999px',
                whiteSpace: 'nowrap'
              }}
            >
              <Globe className="w-3.5 h-3.5 text-sky-400 shrink-0" />
              <span>keep.cruisingalong.com</span>
            </div>
          </div>

          {/* App Store & Google Play Callout */}
          <div 
            style={{
              marginTop: '12px',
              paddingTop: '10px',
              borderTop: '1px solid rgba(255, 255, 255, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)', letterSpacing: '0.01em' }}>
              Available on iOS & Android
            </span>

            <div className="flex items-center gap-2">
              {/* Apple App Store badge */}
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '3px 8px',
                  borderRadius: '6px',
                  background: 'rgba(255, 255, 255, 0.12)',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  color: '#ffffff',
                  fontSize: '10px',
                  fontWeight: 600
                }}
              >
                <svg className="w-3 h-3 fill-current" viewBox="0 0 384 512">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z"/>
                </svg>
                <span>App Store</span>
              </div>

              {/* Google Play badge */}
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '3px 8px',
                  borderRadius: '6px',
                  background: 'rgba(255, 255, 255, 0.12)',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  color: '#ffffff',
                  fontSize: '10px',
                  fontWeight: 600
                }}
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none">
                  <path d="M3.609 1.814L13.793 12 3.61 22.186A2.215 2.215 0 0 1 3 20.615V3.385c0-.608.225-1.168.609-1.571z" fill="#00D3FF"/>
                  <path d="M17.207 8.586L13.793 12l3.414 3.414 4.093-2.34a1.233 1.233 0 0 0 0-2.148l-4.093-2.34z" fill="#FFCE00"/>
                  <path d="M13.793 12L3.609 1.814c.371-.389.914-.614 1.516-.614.475 0 .949.141 1.359.375l10.723 6.125L13.793 12z" fill="#00E676"/>
                  <path d="M13.793 12l3.414 3.414L5.125 21.539c-.41.234-.884.375-1.359.375-.602 0-1.145-.225-1.516-.614L13.793 12z" fill="#FF334B"/>
                </svg>
                <span>Google Play</span>
              </div>
            </div>
          </div>
        </div>

        {/* 3) Bottom Across Tight Glass Card: Ship, Departure & Port Info */}
        <div
          style={{
            position: 'absolute',
            bottom: '32px',
            left: '32px',
            right: '32px',
            background: 'rgba(10, 25, 47, 0.76)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.22)',
            borderRadius: '20px',
            padding: '14px 28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            boxSizing: 'border-box',
            zIndex: 10
          }}
        >
          {/* Left: Ship & Cruise Line */}
          <div>
            <div 
              style={{ 
                fontSize: '26px', 
                fontWeight: 900, 
                color: '#ffffff', 
                lineHeight: 1.15,
                textShadow: '0 2px 10px rgba(0,0,0,0.5)' 
              }}
            >
              {cruise.shipName}
            </div>
            <div 
              style={{ 
                fontSize: '15px', 
                fontWeight: 600, 
                color: '#93c5fd', 
                marginTop: '2px' 
              }}
            >
              {cruise.cruiseLine}
            </div>
          </div>

          {/* Right: Date & Port Metadata Chips */}
          <div className="flex items-center gap-3">
            <div 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 14px',
                borderRadius: '10px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              <Calendar className="w-4 h-4 text-sky-400 shrink-0" />
              <span>Departing {formatDate(parseISO(cruise.departureDate), { fullMonth: true })}</span>
            </div>

            <div 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 14px',
                borderRadius: '10px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              <MapPin className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>From {cruise.departurePort}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

ShareCountdownImageFeed.displayName = 'ShareCountdownImageFeed';

export default ShareCountdownImageFeed;
